import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { errorMessage } from '../api/client';
import {
  billNumberOf,
  isSellable,
  repricedMrpOf,
  itemCodeOf,
  itemTitleOf,
  itemUnitPriceOf,
  paymentMethodOf,
  couponWorth,
  posApi,
  saleCouponOf,
  saleDiscountOf,
  saleTotalOf,
  stockCountOf,
  stockLabelOf,
  type PosBook,
  type PosSale,
} from '../api/pos';
import { money } from '../format';
import PasswordField from './PasswordField';
import TenderPicker, {
  tenderFromSale,
  tenderLabel,
  tenderPayload,
  tenderProblem,
  type TenderValue,
} from './TenderPicker';

/** The part of a bill's discount the counter gave by hand, without its coupon. */
function handDiscountOf(sale: PosSale): number {
  const off = saleDiscountOf(sale) - (saleCouponOf(sale)?.amount ?? 0);
  return off > 0 ? Math.round(off * 100) / 100 : 0;
}

/**
 * One line of the bill being edited.
 *
 * `stock` is what the shelf holds, and it is only known for books added here
 * from a search — a line that was already on the bill arrives with no stock
 * figure attached, because the bill records what was sold, not what is left.
 * So the cap below applies to added books only, and the server is the one that
 * refuses everything else. That is the right way round: it holds the single
 * shared figure the website is selling from at the same moment.
 */
interface EditLine {
  bookId: string;
  title: string;
  code: string;
  unitPrice: number;
  quantity: number;
  stock: number | null;
}

const SEARCH_DEBOUNCE_MS = 180;

function linesFromSale(sale: PosSale): EditLine[] {
  return (sale.items ?? []).map((item) => ({
    bookId: item.bookId ?? item.book?.id ?? '',
    title: itemTitleOf(item),
    code: itemCodeOf(item),
    unitPrice: itemUnitPriceOf(item),
    quantity: item.quantity,
    stock: null,
  }));
}

/**
 * Editing a bill the customer has already paid.
 *
 * The everyday version of this is small: they took one copy and were billed
 * for two, or a book was scanned twice, or the tender was rung as Cash when
 * the machine took it. Before this existed the only answer was to find an
 * admin and have the whole bill voided, which is a heavy thing to do to a sale
 * that mostly happened.
 *
 * Two steps, on purpose. The first is the bill itself, edited the same way it
 * was built. The second is what it costs: the old total, the new one, and a
 * plain sentence about the money to hand back or collect — then the reason,
 * and the staff member's own password. A till is a shared PC left signed in
 * all day, so changing a paid bill should not be one click by whoever is
 * standing at it.
 */
export default function EditBill({
  sale,
  onCancel,
  onSaved,
}: {
  sale: PosSale;
  onCancel: () => void;
  onSaved: (updated: PosSale) => void;
}) {
  const [step, setStep] = useState<'lines' | 'confirm'>('lines');
  const [lines, setLines] = useState<EditLine[]>(() => linesFromSale(sale));
  const [tender, setTender] = useState<TenderValue>(() =>
    tenderFromSale(sale.paymentSplits, paymentMethodOf(sale)),
  );
  // Carried in from the bill rather than starting blank. A discount the
  // customer was already given is part of what they agreed, and an edit that
  // silently dropped it would charge them for it on the strength of a
  // correction to something else entirely.
  //
  // Only the counter's own share goes in the box. A coupon on the bill stays
  // on it, and is worked out again below on whatever the bill now comes to.
  const [discountText, setDiscountText] = useState(() => {
    const given = handDiscountOf(sale);
    return given > 0 ? String(given) : '';
  });
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PosBook[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Which result the arrow keys have moved to, or -1 for none. Same meaning as
  // on the billing screen, and for the same reason: a scan leaves it at -1, so
  // Enter can tell a barcode apart from a row staff have picked.
  const [activeIndex, setActiveIndex] = useState(-1);

  const searchRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const requestSeq = useRef(0);

  const wasTotal = saleTotalOf(sale);
  const wasDiscount = handDiscountOf(sale);
  const wasAllOff = saleDiscountOf(sale);
  const wasCoupon = saleCouponOf(sale);
  const nowSubtotal = useMemo(
    () => lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0),
    [lines],
  );

  // What the bill's coupon is worth after this change: worked out from its
  // terms, the way the server will; or, when the coupon has since been
  // deleted, the amount the bill already carries, which is what the server
  // keeps. Its minimum is checked again — taking books off can drop the bill
  // below it.
  const nowCoupon = !wasCoupon
    ? 0
    : sale.coupon
      ? couponWorth(sale.coupon, nowSubtotal)
      : wasCoupon.amount;
  const couponMin = sale.coupon?.minOrderValue != null ? Number(sale.coupon.minOrderValue) : null;
  const couponProblem =
    !wasCoupon || !lines.length
      ? null
      : couponMin != null && nowSubtotal < couponMin
        ? `This bill used the coupon ${wasCoupon.code}, which needs a bill of at least ${money(couponMin)}. Keep the bill at that or more.`
        : nowCoupon >= nowSubtotal
          ? `The coupon ${wasCoupon.code} would now cover the whole bill. A bill has to come to something.`
          : null;
  const afterCoupon = nowSubtotal - nowCoupon;

  // Face value, never clamped — the same rule as the billing screen and for
  // the same reason. It matters more here, because taking lines off a bill can
  // leave a discount that used to fit larger than what is left of it, and
  // rounding it down would rewrite what the customer was given without saying
  // so.
  const typedDiscount = Number(discountText || 0);
  const discount =
    Number.isFinite(typedDiscount) && typedDiscount > 0 ? typedDiscount : 0;

  /** Why this discount cannot be saved, in the words staff should read. */
  const discountProblem =
    discount > 0 && lines.length
      ? discount > afterCoupon
        ? `That is more than the books left on this bill (${money(afterCoupon)}).`
        : discount === afterCoupon
          ? 'That leaves nothing to pay. A bill has to come to something — give a little less, or ask an admin to cancel it instead of emptying it this way.'
          : null
      : null;

  const discountApplied = discountProblem ? 0 : discount;
  const nowTotal = afterCoupon - discountApplied;

  const difference = nowTotal - wasTotal;
  const wasMethod = paymentMethodOf(sale);
  const wasTender = useMemo(
    () => tenderFromSale(sale.paymentSplits, wasMethod),
    [sale.paymentSplits, wasMethod],
  );
  const tenderChanged =
    tender.split !== wasTender.split ||
    (tender.split
      ? JSON.stringify(tenderPayload(tender)) !== JSON.stringify(tenderPayload(wasTender))
      : tender.method !== wasTender.method);
  const changed =
    difference !== 0 ||
    tenderChanged ||
    discountApplied !== wasDiscount ||
    lines.length !== (sale.items?.length ?? 0) ||
    lines.some((line) => {
      const original = (sale.items ?? []).find(
        (item) => (item.bookId ?? item.book?.id) === line.bookId,
      );
      return !original || original.quantity !== line.quantity;
    });

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  // The scanner types wherever focus is, so on the line-editing step it belongs
  // in the search box — a scan is how staff add the book that was missed.
  useEffect(() => {
    if (step === 'lines') searchRef.current?.focus();
    else reasonRef.current?.focus();
  }, [step]);

  // A new list, or a newly typed term, is a new question — nothing is chosen
  // on it until staff say so. The term matters as much as the list because
  // results lag typing by the debounce below, and a highlight left pointing
  // into the previous list is one Enter away from adding the wrong book.
  useEffect(() => {
    setActiveIndex(-1);
  }, [results, query]);

  // This list scrolls inside the dialog, so arrowing past the fold has to
  // bring the row with it. `nearest` does nothing when it is already visible.
  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`edit-result-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    if (step !== 'lines') return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const seq = ++requestSeq.current;
    setSearching(true);

    const timer = window.setTimeout(() => {
      posApi
        .lookup(q, controller.signal)
        .then((books) => {
          if (seq !== requestSeq.current) return;
          setResults(books ?? []);
          setSearchError(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || seq !== requestSeq.current) return;
          setResults([]);
          setSearchError(errorMessage(err, 'Search failed.'));
        })
        .finally(() => {
          if (seq === requestSeq.current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, step]);

  const addBook = useCallback((book: PosBook) => {
    setError(null);
    setLines((prev) => {
      const idx = prev.findIndex((line) => line.bookId === book.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [
        ...prev,
        {
          bookId: book.id,
          title: book.title,
          code: book.sku ?? book.isbn13 ?? '',
          // A book added now has no history on this bill, so it goes on at
          // today's price. The lines already here keep what they were sold at
          // — a shelf price that moved since is not a reason to re-charge
          // somebody who bought before it did.
          unitPrice: Number(book.price),
          quantity: 1,
          stock: stockCountOf(book),
        },
      ];
    });
    setQuery('');
    setResults([]);
    searchRef.current?.focus();
  }, []);

  /**
   * Where an arrow key lands, skipping what cannot be sold — a sold-out row is
   * a disabled button, and stopping on one would highlight a line Enter then
   * refuses to add. Above the first result the highlight comes off; below the
   * last it stays put rather than wrapping round.
   */
  function stepActive(from: number, step: 1 | -1): number {
    let i = from + step;
    while (i >= 0 && i < results.length && !isSellable(results[i])) i += step;
    if (i < 0) return -1;
    if (i >= results.length) return from;
    return i;
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // Down and up walk the results. preventDefault keeps the caret from
    // jumping to the ends of what has been typed.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!results.length) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => stepActive(i, step));
      return;
    }

    if (e.key !== 'Enter') return;
    e.preventDefault();

    // A row staff have moved to is a decision already made — it settles the
    // ambiguity the rule below deliberately refuses to guess at.
    const picked = activeIndex >= 0 ? results[activeIndex] : undefined;
    if (picked) {
      addBook(picked);
      return;
    }

    // Only an unambiguous single match goes on by itself. Guessing between two
    // results here would put the wrong book on somebody's bill twice over —
    // once on the bill, once on the shelf count.
    if (results.length === 1 && isSellable(results[0])) addBook(results[0]);
  }

  function setQuantity(bookId: string, quantity: number) {
    setError(null);
    setLines((prev) =>
      prev.flatMap((line) => {
        if (line.bookId !== bookId) return [line];
        if (quantity <= 0) return [];
        const cap = line.stock ?? Number.POSITIVE_INFINITY;
        return [{ ...line, quantity: Math.min(quantity, cap) }];
      }),
    );
  }

  function removeLine(bookId: string) {
    setError(null);
    setLines((prev) => prev.filter((line) => line.bookId !== bookId));
    searchRef.current?.focus();
  }

  function review() {
    if (!lines.length) {
      // Not a technicality — an empty bill is a different act with different
      // consequences, and it is not one the counter is allowed to perform.
      setError(
        'A bill cannot be emptied here. Removing everything cancels the bill, and only an admin can cancel a bill.',
      );
      return;
    }
    if (couponProblem) {
      setError(couponProblem);
      return;
    }
    if (discountProblem) {
      setError(`That discount cannot be given. ${discountProblem}`);
      return;
    }
    const splitProblem = tenderProblem(tender, nowTotal);
    if (splitProblem) {
      setError(splitProblem);
      return;
    }
    if (!changed) {
      setError('Nothing has changed on this bill yet.');
      return;
    }
    setError(null);
    setStep('confirm');
  }

  async function save() {
    if (saving) return;
    if (!reason.trim()) {
      setError('Say why the bill is being changed.');
      reasonRef.current?.focus();
      return;
    }
    if (!password) {
      setError('Enter your password to save the change.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await posApi.editSale(sale.id, {
        items: lines.map((line) => ({ bookId: line.bookId, quantity: line.quantity })),
        ...tenderPayload(tender),
        // Sent whenever this bill has anything to say about a discount —
        // including a 0 that takes one off, because leaving it out would mean
        // the server kept a discount staff had just removed in front of the
        // customer. A bill that never had one sends nothing, so an everyday
        // correction still works against a server that predates the field.
        ...(discountApplied > 0 || wasDiscount > 0
          ? { discount: Number(discountApplied.toFixed(2)) }
          : {}),
        reason: reason.trim(),
        password,
      });
      onSaved(updated);
    } catch (err) {
      // Everything worth saying here is said by the server: the book that ran
      // short, the window that has closed, the password that did not match.
      setError(errorMessage(err, 'The bill could not be changed.'));
      setPassword('');
    } finally {
      setSaving(false);
    }
  }

  const settlement =
    difference < 0
      ? `${money(-difference)} to hand back to the customer`
      : difference > 0
        ? `${money(difference)} to collect from the customer`
        : 'No money changes hands';

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        // Stops here rather than bubbling: this dialog opens over the bill
        // view, whose own backdrop would otherwise close that too and take the
        // half-typed edit with it.
        e.stopPropagation();
        if (!saving) onCancel();
      }}
    >
      <div
        className="modal modal-edit"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-bill-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="edit-bill-title" className="confirm-title">
          Edit bill {billNumberOf(sale)}
        </h2>

        {step === 'lines' ? (
          <>
            <p className="muted small confirm-lede">
              Change the bill to what the customer actually took. The money is settled
              across the counter by hand — this records it.
            </p>

            <label className="field-label" htmlFor="edit-scan">
              Add a book that was missed
            </label>
            <input
              id="edit-scan"
              ref={searchRef}
              className="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Scan a barcode, or type a title / ISBN / SKU"
              autoComplete="off"
              spellCheck={false}
              // Walked from here rather than tabbed into, so the box keeps the
              // focus a scan needs while the arrows move a highlight inside
              // the list. The focus never moves, so aria-activedescendant is
              // what carries that highlight to a screen reader.
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls="edit-results"
              aria-autocomplete="list"
              aria-activedescendant={
                activeIndex >= 0 ? `edit-result-${activeIndex}` : undefined
              }
            />

            {(searching || searchError || results.length > 0) && (
              <div className="results results-inline">
                <div className="results-status" aria-live="polite">
                  {searching && <p className="muted">Searching…</p>}
                  {searchError && <p className="error">{searchError}</p>}
                </div>

                <div
                  id="edit-results"
                  className="results-list"
                  role="listbox"
                  aria-label="Search results"
                >
                  {results.map((book, i) => {
                    const soldOut = !isSellable(book);
                    const active = i === activeIndex;
                    return (
                      <button
                        key={book.id}
                        id={`edit-result-${i}`}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={
                          soldOut
                            ? 'result result-out'
                            : active
                              ? 'result result-active'
                              : 'result'
                        }
                        onClick={() => addBook(book)}
                        disabled={soldOut}
                        // `disabled` is not carried by the option role, so
                        // without this a sold-out row reads as billable.
                        aria-disabled={soldOut}
                      >
                        <span className="result-main">
                          <span className="result-title">
                            {/* Wrapped so a long title ellipsises inside the
                                column instead of pushing the price out of it,
                                and hover gives the whole name back. */}
                            <span className="title-text" title={book.title}>
                              {book.title}
                            </span>
                            {book.isDCopy ? (
                              <span className="tag tag-dcopy">D-COPY</span>
                            ) : null}
                            {book.isUsed ? (
                              <span className="tag tag-used">USED</span>
                            ) : null}
                            {repricedMrpOf(book, results) !== null ? (
                              <span className="tag tag-mrp">
                                MRP {money(repricedMrpOf(book, results))}
                              </span>
                            ) : null}
                          </span>
                          <span className="result-code">{book.sku ?? book.isbn13 ?? ''}</span>
                        </span>
                        <span className="result-right">
                          <span className="result-price">{money(book.price)}</span>
                          <span className={soldOut ? 'stock stock-out' : 'stock'}>
                            {stockLabelOf(book)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <ul className="lines">
              {lines.map((line) => (
                <li key={line.bookId} className="line">
                  <div className="line-info">
                    <span className="line-title">
                      <span className="title-text" title={line.title}>
                        {line.title}
                      </span>
                    </span>
                    <span className="line-unit">
                      {money(line.unitPrice)} each{line.code ? ` · ${line.code}` : ''}
                    </span>
                  </div>
                  <div className="qty">
                    <button
                      type="button"
                      className="qty-btn"
                      onClick={() => setQuantity(line.bookId, line.quantity - 1)}
                      aria-label={`Reduce quantity of ${line.title}`}
                    >
                      −
                    </button>
                    <input
                      className="qty-input"
                      inputMode="numeric"
                      value={line.quantity}
                      onChange={(e) =>
                        setQuantity(
                          line.bookId,
                          Number(e.target.value.replace(/\D/g, '') || 0),
                        )
                      }
                      aria-label={`Quantity of ${line.title}`}
                    />
                    <button
                      type="button"
                      className="qty-btn"
                      onClick={() => setQuantity(line.bookId, line.quantity + 1)}
                      disabled={line.quantity >= (line.stock ?? Number.POSITIVE_INFINITY)}
                      aria-label={`Increase quantity of ${line.title}`}
                    >
                      +
                    </button>
                  </div>
                  <span className="line-total">{money(line.unitPrice * line.quantity)}</span>
                  <button
                    type="button"
                    className="remove"
                    onClick={() => removeLine(line.bookId)}
                    aria-label={`Remove ${line.title}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>

            {lines.length === 0 && (
              <p className="muted empty">
                Every line has been taken off. Put one back, or close this and ask an
                admin to void the bill.
              </p>
            )}

            <TenderPicker
              idPrefix="edit-tender"
              value={tender}
              total={nowTotal}
              onChange={setTender}
              showProblem
            />

            <div className="discount">
              <label className="field-label" htmlFor="edit-discount">
                Discount
              </label>
              <div className="discount-row">
                <span className="discount-rupee" aria-hidden="true">
                  ₹
                </span>
                <input
                  id="edit-discount"
                  className="input discount-input"
                  inputMode="decimal"
                  value={discountText}
                  onChange={(e) => {
                    const cleaned = e.target.value
                      .replace(/[^\d.]/g, '')
                      .replace(/(\..*)\./g, '$1')
                      .replace(/^(\d*\.\d{0,2}).*$/, '$1');
                    setError(null);
                    setDiscountText(cleaned);
                  }}
                  onKeyDown={(e) => {
                    // The scanner sends Enter after its code; keep it out of
                    // this box and back in the search field above.
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    searchRef.current?.focus();
                  }}
                  placeholder="0"
                  autoComplete="off"
                />
                {discountText !== '' && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      setDiscountText('');
                      setError(null);
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p
                className={discountProblem ? 'error small' : 'muted small'}
                role={discountProblem ? 'alert' : undefined}
              >
                {couponProblem ??
                  discountProblem ??
                  (wasDiscount > 0
                    ? `This bill was given ${money(wasDiscount)} off. Change it or take it away here.`
                    : 'Rupees off the whole bill. Leave blank if there is none.')}
              </p>
            </div>

            <div className="edit-diff">
              <div className="edit-diff-row">
                <span>Was</span>
                <span>{money(wasTotal)}</span>
              </div>
              {(discountApplied > 0 || nowCoupon > 0) && (
                <>
                  <div className="edit-diff-row edit-diff-sub">
                    <span>Items</span>
                    <span>{money(nowSubtotal)}</span>
                  </div>
                  {nowCoupon > 0 && (
                    <div className="edit-diff-row edit-diff-sub">
                      <span>Coupon {wasCoupon?.code}</span>
                      <span>− {money(nowCoupon)}</span>
                    </div>
                  )}
                  {discountApplied > 0 && (
                    <div className="edit-diff-row edit-diff-sub">
                      <span>Discount</span>
                      <span>− {money(discountApplied)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="edit-diff-row edit-diff-now">
                <span>Now</span>
                <strong>{money(nowTotal)}</strong>
              </div>
            </div>

            {error && (
              <p className="error error-block" role="alert">
                {error}
              </p>
            )}

            <div className="modal-actions">
              <button type="button" className="btn" onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={review}>
                Review change
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="muted small confirm-lede">
              Read this back before saving. The bill can only be edited once at the
              counter — after this, changing it again means asking an admin.
            </p>

            <ul className="confirm-lines">
              {lines.map((line) => (
                <li key={line.bookId}>
                  <span className="confirm-line-title">{line.title}</span>
                  <span className="confirm-line-qty">
                    {line.quantity} × {money(line.unitPrice)}
                  </span>
                  <span className="confirm-line-total">
                    {money(line.unitPrice * line.quantity)}
                  </span>
                </li>
              ))}
            </ul>

            <div className="edit-diff">
              <div className="edit-diff-row">
                <span>Was</span>
                <span>
                  {money(wasTotal)}
                  {wasAllOff > 0 ? ` (after ${money(wasAllOff)} off)` : ''}
                  {wasMethod ? ` · ${tenderLabel(wasTender)}` : ''}
                </span>
              </div>
              {(discountApplied > 0 || nowCoupon > 0) && (
                <>
                  <div className="edit-diff-row edit-diff-sub">
                    <span>Items</span>
                    <span>{money(nowSubtotal)}</span>
                  </div>
                  {nowCoupon > 0 && (
                    <div className="edit-diff-row edit-diff-sub">
                      <span>Coupon {wasCoupon?.code}</span>
                      <span>− {money(nowCoupon)}</span>
                    </div>
                  )}
                  {discountApplied > 0 && (
                    <div className="edit-diff-row edit-diff-sub">
                      <span>Discount</span>
                      <span>− {money(discountApplied)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="edit-diff-row edit-diff-now">
                <span>Now</span>
                <strong>
                  {money(nowTotal)} · {tenderLabel(tender)}
                </strong>
              </div>
            </div>

            <p className={difference === 0 ? 'edit-settle' : 'edit-settle edit-settle-money'}>
              {settlement}
            </p>

            <label className="field-label" htmlFor="edit-reason">
              Why is this bill being changed?
            </label>
            <textarea
              id="edit-reason"
              ref={reasonRef}
              className="input edit-reason"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="Customer took one copy, not two"
            />

            <label className="field-label" htmlFor="edit-password">
              Your password
            </label>
            <PasswordField
              id="edit-password"
              value={password}
              onChange={setPassword}
              placeholder="Password for the account signed in here"
            />
            <p className="muted small">
              The till is a shared machine, so a paid bill does not change on one click.
            </p>

            {error && (
              <p className="error error-block" role="alert">
                {error}
              </p>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setError(null);
                  setStep('lines');
                }}
                disabled={saving}
              >
                Go back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void save()}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save edit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
